import { el } from './dom';
import { listGames } from '@core/game-registry';
import { deleteSession, listActiveSessions } from '@core/session';
import { t, getLang, setLang } from '@core/i18n';
import { getStorageConsent } from '@core/privacy';
import { navigate } from './router';
import { assetUrl } from './assets';
import { openConsentSettings } from './privacy';

export async function renderLobby(): Promise<HTMLElement> {
  const games = listGames();
  const active = await listActiveSessions();

  const langSwitch = el('div', { class: 'flex gap-2 text-sm' }, [
    el(
      'button',
      {
        class: getLang() === 'it' ? 'chip bg-accent text-slate-900' : 'chip',
        on: {
          click: () => {
            setLang('it');
            location.reload();
          },
        },
      },
      ['IT'],
    ),
    el(
      'button',
      {
        class: getLang() === 'en' ? 'chip bg-accent text-slate-900' : 'chip',
        on: {
          click: () => {
            setLang('en');
            location.reload();
          },
        },
      },
      ['EN'],
    ),
  ]);

  const headerControls = el('div', { class: 'flex flex-col items-end gap-2' }, [
    langSwitch,
    el(
      'button',
      {
        class: 'chip hover:bg-slate-700 transition',
        on: { click: () => openConsentSettings({ onChange: () => navigate({ name: 'lobby' }) }) },
      },
      [t('privacy.manage')],
    ),
  ]);

  const header = el('header', { class: 'flex items-start justify-between gap-3 mb-6' }, [
    el('div', {}, [
      el('h1', { class: 'text-3xl font-bold tracking-tight' }, [t('app.title')]),
      el('p', { class: 'text-slate-400' }, [t('app.tagline')]),
    ]),
    headerControls,
  ]);

  const gamesGrid = el(
    'div',
    { class: 'grid gap-4 sm:grid-cols-2' },
    games.map((g) =>
      el(
        'button',
        {
          class: 'card text-left hover:ring-accent/40 transition',
          dataset: { game: g.id },
          on: {
            click: () => navigate({ name: 'setup', gameId: g.id }),
          },
        },
        [
          el('div', { class: 'flex items-center gap-3 mb-2' }, [
            g.metadata.iconUrl
              ? (() => { const img = document.createElement('img'); img.src = assetUrl(g.metadata.iconUrl); img.alt = ''; img.className = 'w-10 h-10 object-contain shrink-0'; return img; })()
              : el('span', { class: 'text-3xl' }, [g.metadata.icon]),
            el('h2', { class: 'text-xl font-semibold' }, [g.metadata.name]),
          ]),
          el('p', { class: 'text-slate-400 text-sm' }, [g.metadata.description]),
        ],
      ),
    ),
  );

  const storageNotice = getStorageConsent() === 'rejected'
    ? el('div', { class: 'mt-4 rounded-xl border border-amber-500/30 bg-amber-950/30 p-3 text-sm text-amber-100' }, [
        t('lobby.storage_ephemeral'),
      ])
    : null;

  const resumeSection = active.length
    ? el('section', { class: 'mt-8' }, [
        el('h3', { class: 'text-lg font-semibold mb-3' }, [t('common.resume')]),
        el(
          'div',
          { class: 'grid gap-2' },
          active.map((s) => {
            const game = games.find((g) => g.id === s.gameType);
            const id = s.gameId.replace(/^.*::/, '');
            return el(
              'div',
              {
                class: 'card flex items-center gap-3 p-3 sm:p-4',
              },
              [
                el(
                  'button',
                  {
                    class: 'flex min-w-0 flex-1 items-center justify-between gap-3 text-left',
                    on: {
                      click: () =>
                        navigate({ name: 'play', gameId: s.gameType, sessionId: id }),
                    },
                  },
                  [
                    el('div', { class: 'min-w-0' }, [
                      el('div', { class: 'font-medium flex items-center gap-1 min-w-0' }, [
                        game?.metadata.iconUrl
                          ? (() => { const img = document.createElement('img'); img.src = assetUrl(game.metadata.iconUrl); img.alt = ''; img.className = 'w-5 h-5 object-contain shrink-0'; return img; })()
                          : document.createTextNode(`${game?.metadata.icon ?? '?'} `),
                        el('span', { class: 'truncate' }, [game?.metadata.name ?? s.gameType]),
                      ]),
                      el('div', { class: 'text-xs text-slate-400' }, [
                        new Date(s.updatedAt).toLocaleString(),
                      ]),
                    ]),
                    el('span', { class: 'chip bg-accent/20 text-accent shrink-0' }, [
                      t('turn.attempt', { n: s.history.length }),
                    ]),
                  ],
                ),
                el(
                  'button',
                  {
                    class: 'btn-ghost px-3 py-2 text-sm shrink-0',
                    title: t('lobby.delete_session'),
                    on: {
                      click: async () => {
                        if (!confirm(t('lobby.delete_session.confirm'))) return;
                        await deleteSession(s.gameType, id);
                        navigate({ name: 'lobby' });
                      },
                    },
                  },
                  ['×'],
                ),
              ],
            );
          }),
        ),
      ])
    : null;

  return el('main', { class: 'mx-auto max-w-2xl p-4 sm:p-6' }, [
    header,
    el('h2', { class: 'text-lg font-semibold mb-3 text-slate-300' }, [t('lobby.choose')]),
    gamesGrid,
    storageNotice,
    resumeSection,
  ]);
}
